/* eslint-disable jsx-a11y/media-has-caption */
import { useEffect, useState, useRef, type FunctionComponent } from 'react';
import {
  Button,
  Field,
  Input,
  Switch,
  Text,
  shorthands,
  makeStyles,
  Toolbar,
  ToolbarButton,
  tokens,
  Label,
} from '@fluentui/react-components';
import {
  Camera24Regular,
  ZoomInRegular,
  PlayCircleRegular,
  DeleteRegular,
  Video24Regular,
  Dismiss12Filled,
  DismissCircleRegular,
} from '@fluentui/react-icons';
import {
  assertRecordedMp4,
  createMp4VideoRecorder,
  formatDate,
  parseUtcDate,
  readVideoBlobAsDataUrl,
  type IWorkImage,
  type IWorkImageContent,
} from '@moko/utils';
import { isContractorRole } from '@moko/utils/src/authHelper';
import { ImagePreviewDialog } from '../ImagePreviewDialog';
import { PhotoEditor } from '../PhotoEditor';
import { notify } from '../Notify';
import type { WorkComponentProps, IWorkPhotosServices } from './types';
import ContentSkeleton from '../ContentSkeleton/ContentSkeleton';

interface WorkPhotosProps extends WorkComponentProps {
  workId: number;
  services: IWorkPhotosServices;
  onBackToDetails: () => void;
  readOnly?: boolean;
}

interface Photo {
  createdAt: Date;
  id: number;
  thumbnailContent: string;
  fullImageUrl: string;
  type: string;
  caption?: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexWrap: 'wrap',
    ...shorthands.gap('12px'),
  },
  photoItem: {
    width: '120px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('8px'),
    ...shorthands.padding('8px'),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
    ...shorthands.gap('8px'),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transitionProperty: 'transform',
    transitionDuration: '0.2s',
    transitionTimingFunction: 'ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  thumbnail: {
    width: '100px',
    height: '100px',
    objectFit: 'cover',
    ...shorthands.borderRadius('4px'),
  },
  caption: {
    width: '100%',
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  button: {
    minWidth: 'unset',
  },
  iconCancel: {
    color: tokens.colorPaletteRedForeground2,
    fontSize: '64px',
  },
  cameraRow: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap('12px'),
  },
  cameraActions: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  uploadActions: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
});

const WorkPhotos: FunctionComponent<WorkPhotosProps> = ({
  workId,
  services,
  onBackToDetails,
  readOnly = false,
}) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [mode, setMode] = useState<'photo' | 'video'>('photo');
  const [showCamera, setShowCamera] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  const [photoEditing, setPhotoEditing] = useState(false);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [location, setLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const [recording, setRecording] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);

  const [refreshCount, setRefreshCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogContent, setDialogContent] = useState<IWorkImageContent | null>(
    null
  );

  useEffect(() => {
    const fetchPhotos = async () => {
      const abortController = new AbortController();
      setLoading(true);
      setError(null);
      try {
        const res = await services.getWorkImages(abortController, workId);
        setPhotos(
          res.items.map((p: IWorkImage) => ({
            id: p.id,
            createdAt: p.createdAt ? parseUtcDate(p.createdAt) : new Date(),
            thumbnailContent: p.imageThumbnail,
            fullImageUrl: p.imageUrl,
            type: p.type,
            caption: p.caption?.trim() || undefined,
          }))
        );
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, [workId, refreshCount]);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const startCamera = async () => {
    try {
      const videoConstraints =
        mode === 'video'
          ? { width: { ideal: 1920 }, height: { ideal: 1080 } }
          : true;
      let mediaStream: MediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: mode === 'video',
        });
      } catch (mediaError) {
        if (mode !== 'video') throw mediaError;
        console.warn(
          '[WorkPhotos] Microphone unavailable; recording silent MP4 video:',
          mediaError
        );
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });
      }
      setStream(mediaStream);
    } catch {
      notify.error('Unable to access camera');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setLocation({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          }),
        () => setLocation(null)
      );
    } else {
      setLocation(null);
    }
  };

  const handleOpenCamera = () => {
    setShowCamera(true);
    startCamera();
  };

  const handleTakePhoto = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      setPreview(canvas.toDataURL('image/jpeg'));
    }
    getLocation();
    stopCamera();
    setShowCamera(false);
  };

  const handleStartRecording = () => {
    if (!videoRef.current || !stream) return;
    recordedChunksRef.current = [];

    let selection;
    try {
      selection = createMp4VideoRecorder(stream);
    } catch (recordingError) {
      notify.error(
        recordingError instanceof Error
          ? recordingError.message
          : 'This browser cannot record MP4 video.'
      );
      return;
    }

    const { recorder: mediaRecorder, requestedMimeType } = selection;
    mediaRecorderRef.current = mediaRecorder;
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) recordedChunksRef.current.push(event.data);
    };
    mediaRecorder.onstop = () => {
      if (recordedChunksRef.current.length === 0) {
        notify.error(
          'The browser did not produce any video data. Please try again.'
        );
        setRecording(false);
        return;
      }

      let mimeType: 'video/mp4';
      try {
        mimeType = assertRecordedMp4(
          mediaRecorder.mimeType,
          requestedMimeType,
          recordedChunksRef.current
        );
      } catch (recordingError) {
        notify.error(
          recordingError instanceof Error
            ? recordingError.message
            : 'The browser did not produce an MP4 video.'
        );
        setRecording(false);
        return;
      }

      const blob = new Blob(recordedChunksRef.current, { type: mimeType });
      setVideoBlob(blob);
      setVideoPreview(URL.createObjectURL(blob));
      getLocation();
      stopCamera();
      setShowCamera(false);
      setRecording(false);
    };
    mediaRecorder.start();
    setRecording(true);
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
    }
  };

  const photoCaptionRequired = isContractorRole();

  const handleUploadPhoto = async () => {
    if (!preview) return;
    const trimmedCaption = caption.trim();
    if (photoCaptionRequired && !trimmedCaption) return;
    setLoading(true);
    try {
      const abortController = new AbortController();
      await services.createWorkImage(abortController, {
        workId,
        imageContent: preview,
        type: 'photo',
        location,
        ...(trimmedCaption ? { caption: trimmedCaption } : {}),
      });
      setPreview(null);
      setCaption('');
      setLocation(null);
      setRefreshCount((prev) => prev + 1);
    } catch (err: any) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadVideo = async () => {
    if (!videoBlob) return;
    setLoading(true);
    try {
      const abortController = new AbortController();
      const imageContent = await readVideoBlobAsDataUrl(videoBlob);
      const trimmedCaption = caption.trim();
      await services.createWorkImage(abortController, {
        workId,
        imageContent,
        type: 'video',
        location,
        ...(trimmedCaption ? { caption: trimmedCaption } : {}),
      });
      setRefreshCount((prev) => prev + 1);
      setVideoPreview(null);
      setVideoBlob(null);
      setCaption('');
      setLocation(null);
    } catch (err: any) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewImage = async (photoId: number) => {
    setDialogOpen(true);
    setDialogLoading(true);
    setDialogError(null);
    setDialogContent(null);
    try {
      const abortController = new AbortController();
      const data = await services.getWorkImage(abortController, photoId);
      setDialogContent(data);
    } catch (err: any) {
      setDialogError(err.message);
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteImage = async (photoId: number) => {
    setLoading(true);
    try {
      const abortController = new AbortController();
      await services.deleteWorkImage(abortController, photoId);
      setRefreshCount((prev) => prev + 1);
    } catch (err: any) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const styles = useStyles();
  const captionsSrc = '/path/to/your/captions.vtt';

  return (
    <div>
      {loading && <ContentSkeleton variant="lines" rows={2} />}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {!showCamera && !preview && !videoPreview && (
        <>
          <Toolbar aria-label="Default">
            <Label>{`Work ${workId} Photos`}</Label>
            <ToolbarButton
              aria-label="Cancel"
              appearance="subtle"
              icon={<DismissCircleRegular className={styles.iconCancel} />}
              onClick={onBackToDetails}
            >
              Cancel
            </ToolbarButton>
          </Toolbar>
          <div className={styles.container}>
            {photos.map((photo) => (
              <div key={photo.id} className={styles.photoItem}>
                <img
                  src={photo.thumbnailContent}
                  alt="Photo"
                  className={styles.thumbnail}
                />
                <div>{formatDate(photo.createdAt, true, true)}</div>
                {photo.caption ? (
                  <Text className={styles.caption} title={photo.caption}>
                    {photo.caption}
                  </Text>
                ) : null}
                <div className={styles.buttonGroup}>
                  <Button
                    icon={
                      photo.type === 'photo' ? (
                        <ZoomInRegular />
                      ) : (
                        <PlayCircleRegular />
                      )
                    }
                    onClick={() => handleViewImage(photo.id)}
                    appearance="subtle"
                    className={styles.button}
                    size="small"
                  >
                    View
                  </Button>
                  {!readOnly && (
                    <Button
                      icon={<DeleteRegular />}
                      onClick={() => handleDeleteImage(photo.id)}
                      appearance="subtle"
                      title="Delete"
                      className={styles.button}
                      size="small"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          {!readOnly && (
            <div
              style={{
                marginTop: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Text>Photo</Text>
              <Switch
                checked={mode === 'video'}
                onChange={(_, data) =>
                  setMode(data.checked ? 'video' : 'photo')
                }
                label="Video"
              />
              <Button
                icon={
                  mode === 'photo' ? <Camera24Regular /> : <Video24Regular />
                }
                onClick={handleOpenCamera}
                appearance="primary"
              >
                {mode === 'photo' ? 'Take Photo' : 'Record Video'}
              </Button>
            </div>
          )}
        </>
      )}

      {showCamera && (
        <div className={styles.cameraRow}>
          <div>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{ width: 320, height: 240 }}
            />
            <track kind="captions" src={captionsSrc} label="English" />
          </div>
          <div className={styles.cameraActions}>
            {mode === 'photo' ? (
              <Button onClick={handleTakePhoto} appearance="primary">
                Capture
              </Button>
            ) : !recording ? (
              <Button onClick={handleStartRecording} appearance="primary">
                Start Recording
              </Button>
            ) : (
              <Button onClick={handleStopRecording} appearance="primary">
                Stop Recording
              </Button>
            )}
            <Button
              icon={<Dismiss12Filled />}
              onClick={() => {
                stopCamera();
                setShowCamera(false);
                setRecording(false);
              }}
              appearance="secondary"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {preview && (
        <div>
          <PhotoEditor
            src={preview}
            onSrcChange={setPreview}
            onEditingChange={setPhotoEditing}
          />
          <div className={styles.uploadActions}>
            <Field label="Caption" required={photoCaptionRequired}>
              <Input
                value={caption}
                disabled={photoEditing}
                onChange={(_, data) => setCaption(data.value)}
              />
            </Field>
            <Button
              onClick={handleUploadPhoto}
              appearance="primary"
              disabled={
                photoEditing || (photoCaptionRequired && !caption.trim())
              }
            >
              Upload
            </Button>
          </div>
        </div>
      )}

      {videoPreview && (
        <div>
          <video
            src={videoPreview}
            controls
            style={{ width: 320, height: 240 }}
          />
          <track kind="captions" src={captionsSrc} label="English" />
          <div className={styles.uploadActions}>
            <Field label="Caption">
              <Input
                value={caption}
                onChange={(_, data) => setCaption(data.value)}
              />
            </Field>
            <Button onClick={handleUploadVideo} appearance="primary">
              Upload
            </Button>
          </div>
        </div>
      )}

      <ImagePreviewDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        loading={dialogLoading}
        error={dialogError}
        content={dialogContent}
        captionsSrc={captionsSrc}
        labels={{
          title: 'Photo',
          close: 'Close',
          loading: 'Loading...',
          location: 'Location',
          note: 'Note',
          showAnalytics: 'Show Analytics',
          hideAnalytics: 'Hide Analytics',
          objects: 'Objects',
          persons: 'Persons',
          texts: 'Texts',
        }}
      />
    </div>
  );
};

export default WorkPhotos;
